import {EditorDocStrategyAssociation, EditorDocStrategyType} from 'client';
import {parseAndExtractTextFromLexicalJson} from 'components/DSYS/editor/utils/lexical';

export type StrategyEntry = {
  id: number;
  editor_state: string;
};

export type StrategyNodeType = 'obstacles-and-opportunities' | 'guiding-principles';

// Generate temporary negative IDs for locally-created entries (before saved to DB).
// Negative IDs indicate "not yet persisted" and won't conflict with real DB IDs
let tempIdCounter = -1;
export const createStrategyEntryId = (): number => {
  return tempIdCounter--;
};

export const isStrategyEntry = (value: unknown): value is StrategyEntry =>
  Boolean(
    value &&
    typeof value === 'object' &&
    'id' in value &&
    typeof value.id === 'number' &&
    'editor_state' in value &&
    typeof value.editor_state === 'string'
  );

export const getStrategyEntryPlainText = (entry: StrategyEntry | string): string => {
  if (typeof entry === 'string') {
    return entry.trim();
  }

  return parseAndExtractTextFromLexicalJson(entry.editor_state).trim();
};

/**
 * Creates a stable key for an entry based on its ID (for saved entries) or position + content (for pending entries).
 * Format:
 * - Saved entries (positive ID): `${nodeType}:id:${entry.id}`
 * - Pending entries (negative ID): `${nodeType}:pid:${index}:${contentHash}`
 *
 * For saved entries, the key remains stable across content edits, reordering, and deletions.
 * For pending entries, the key is based on position and content hash. When the entry is saved and
 * receives a permanent ID, the content-based key helps match it to find and migrate comments.
 * Comments persist even when the text is edited (for saved entries only).
 * Used for persisting comments and rich editor state in editorStateMapJson.
 */
export const createStrategyEntryStableKey = (
  entry: StrategyEntry,
  nodeType: StrategyNodeType,
  index: number
): string => {
  if (entry.id > 0) {
    // Saved entries: use ID (most stable)
    return `${nodeType}:id:${entry.id}`;
  } else {
    // Pending entries: use index + content hash
    // This allows comments to survive the transition from negative ID to positive ID
    // by matching on content when the entry gets saved
    const plainText = getStrategyEntryPlainText(entry);
    const hash = Array.from(plainText)
      .reduce((acc, char) => ((acc << 5) - acc + char.charCodeAt(0)) | 0, 0)
      .toString(36);
    return `${nodeType}:pid:${index}:${hash}`;
  }
};

export const normalizeStrategyEntries = (value?: unknown): StrategyEntry[] => {
  if (!value) {
    return [];
  }

  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item): StrategyEntry | null => {
      // Already a StrategyEntry
      if (isStrategyEntry(item)) {
        return item;
      }

      // EditorDocStrategyAssociation
      if (
        item &&
        typeof item === 'object' &&
        'strategy_value' in item &&
        typeof item.strategy_value === 'string' &&
        'id' in item
      ) {
        return {
          id: item.id as number,
          editor_state: item.strategy_value,
        };
      }

      return null;
    })
    .filter((entry): entry is StrategyEntry => entry !== null);
};

export const normalizeStrategySummary = (
  strategyAssociations?: EditorDocStrategyAssociation[] | null
): {
  obstacles_and_opportunities: EditorDocStrategyAssociation[];
  guiding_principles: EditorDocStrategyAssociation[];
} | null => {
  if (!strategyAssociations?.length) {
    return null;
  }

  return {
    obstacles_and_opportunities: strategyAssociations.filter(
      (association) =>
        association.strategy_type === EditorDocStrategyType.OBSTACLES_AND_OPPORTUNITIES
    ),
    guiding_principles: strategyAssociations.filter(
      (association) => association.strategy_type === EditorDocStrategyType.GUIDING_PRINCIPLES
    ),
  };
};

/**
 * Merges DB entries with local entries, preserving comments on entries whose plain text hasn't changed.
 * Uses plain text comparison per entry to decide whether to keep local version (with comments) or take DB version.
 * Also preserves local-only pending entries (negative IDs) that haven't been saved yet.
 */
export const mergeStrategyEntries = (
  localEntries: StrategyEntry[],
  dbEntries: StrategyEntry[]
): StrategyEntry[] => {
  const result: StrategyEntry[] = [];
  const usedLocalIds = new Set<number>();

  // Match DB entries with local entries by plain text
  for (const dbEntry of dbEntries) {
    const dbPlainText = getStrategyEntryPlainText(dbEntry).trim();

    // Find matching local entry by plain text (skip pending entries with negative IDs)
    const matchingLocal = localEntries.find((localEntry) => {
      if (usedLocalIds.has(localEntry.id)) return false;
      if (localEntry.id < 0) return false; // Skip pending entries
      const localPlainText = getStrategyEntryPlainText(localEntry).trim();
      return localPlainText === dbPlainText;
    });

    if (matchingLocal) {
      // Plain text matches - keep local version to preserve comments
      result.push(matchingLocal);
      usedLocalIds.add(matchingLocal.id);
    } else {
      // Plain text changed or new entry - use DB version
      result.push(dbEntry);
    }
  }

  // Add pending entries (negative IDs) that don't match any DB entry
  const dbPlainTexts = new Set(dbEntries.map((e) => getStrategyEntryPlainText(e).trim()));

  for (const localEntry of localEntries) {
    if (localEntry.id < 0 && !usedLocalIds.has(localEntry.id)) {
      const localPlainText = getStrategyEntryPlainText(localEntry).trim();
      // Only add if this text doesn't already exist in DB
      if (!localPlainText || !dbPlainTexts.has(localPlainText)) {
        result.push(localEntry);
        usedLocalIds.add(localEntry.id);
      }
    }
  }

  return result;
};
